import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetAlertsAndGroupsForCurrentUserQuery } from '~/api/generated-api';
import Spinner from '~/common/components/Spinner/Spinner';
import useInfiniteScroll from '~/common/hooks/useInfiniteScroll';
import SeeAllButton from '~/routes/notifications/components/SeeAllButton';
import { selectActingProfileOrganization } from '~/store/profile/profileSlice.selectors';
import { useAlertsPagination } from '../../hooks/useAlertsPagination';
import { PinnedAlertsProvider } from '../../providers/PinnedAlertsProvider';
import NoNotificationCard from '../Cards/NoNotificationCard/NoNotificationCard';
import NotificationList from '../NotificationList/NotificationList';
import { useStyles } from './NotificationPopover.styles';

interface NotificationsPopoverProps {
  onClose: () => void;
}

const NotificationsPopover = ({ onClose }: NotificationsPopoverProps) => {
  const { classes } = useStyles();
  const orgId = useSelector(selectActingProfileOrganization)?.id;

  const [token, setToken] = useState<string | undefined>(undefined);
  const { data, isLoading, isFetching } =
    useGetAlertsAndGroupsForCurrentUserQuery(
      {
        size: 20,
        'cs-continuation-token': token,
        'cs-organization': orgId ?? '',
        dismissed: false,
      },
      { refetchOnMountOrArgChange: true }
    );

  const { alerts, hasMoreAlerts, needsNextPage, handleRemoveNotification } =
    useAlertsPagination({ data });

  const continuationToken = data?.result?.continuation_token;
  useEffect(() => {
    if (needsNextPage && !isFetching && continuationToken) {
      setToken(continuationToken);
    }
  }, [needsNextPage, isFetching, continuationToken]);

  const loadMore = () => {
    if (!hasMoreAlerts || isLoading || !data?.result?.continuation_token) {
      return;
    }
    setToken(data.result.continuation_token);
  };

  const { elementRef } = useInfiniteScroll<HTMLUListElement>({
    callbackFn: loadMore,
    intersectProps: { threshold: 0.5 },
  });

  return (
    <PinnedAlertsProvider>
      <Box
        className={classes['rt--notifications--notifications--box-container']}
      >
        <Box
          className={classes['rt--notifications--notifications-list-container']}
          ref={elementRef}
        >
          {alerts.length ? (
            <NotificationList
              type={'section'}
              context={'popover'}
              items={alerts}
              onDismissNotification={handleRemoveNotification}
              onClose={onClose}
            />
          ) : isLoading || isFetching ? (
            <Spinner show={true} />
          ) : (
            <NoNotificationCard context="popover" />
          )}
        </Box>
        {alerts.length > 0 && <SeeAllButton />}
      </Box>
    </PinnedAlertsProvider>
  );
};

export default NotificationsPopover;
